import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Circle } from 'lucide-react';
import type { AgentStatus } from '../types';

interface StatusSelectorProps {
  currentStatus: AgentStatus;
  onStatusChange: (status: AgentStatus) => void;
}

const statusConfig: Record<AgentStatus, { label: string; color: string; bgColor: string }> = {
  ONLINE: {
    label: 'Online',
    color: 'text-green-600',
    bgColor: 'bg-green-100 hover:bg-green-200',
  },
  AUSENTE: {
    label: 'Ausente',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100 hover:bg-yellow-200',
  },
  OCUPADO: {
    label: 'Ocupado',
    color: 'text-red-600',
    bgColor: 'bg-red-100 hover:bg-red-200',
  },
  EM_REUNIAO: {
    label: 'Em Reunião',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100 hover:bg-purple-200',
  },
};

export default function StatusSelector({ currentStatus, onStatusChange }: StatusSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<AgentStatus>(currentStatus);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Atualiza o estado otimista quando o status real mudar
  useEffect(() => {
    setOptimisticStatus(currentStatus);
  }, [currentStatus]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusChange = (status: AgentStatus) => {
    // Atualiza imediatamente o visual (otimista)
    setOptimisticStatus(status);
    setIsOpen(false);
    // Chama a função de atualização real
    onStatusChange(status);
  };

  const currentConfig = statusConfig[optimisticStatus];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${currentConfig.bgColor}`}
      >
        <Circle className={`w-3 h-3 fill-current ${currentConfig.color} transition-colors duration-200`} />
        <span className={`text-sm font-medium ${currentConfig.color} transition-colors duration-200`}>
          {currentConfig.label}
        </span>
        <ChevronDown className={`w-4 h-4 ${currentConfig.color} transition-all duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {(Object.entries(statusConfig) as [AgentStatus, typeof statusConfig[AgentStatus]][]).map(([status, config]) => (
            <button
              key={status}
              onClick={() => handleStatusChange(status)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                status === currentStatus ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <Circle className={`w-3 h-3 fill-current ${config.color}`} />
              <span className={`text-sm font-medium ${config.color}`}>
                {config.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
